import { RefObject, MutableRefObject } from 'react';

import { GridApiPro } from '@mui/x-data-grid-pro';

import { ScheduleStatus, TaskStatus } from 'utils/api/modules/tasks/tasks.constants';

import { FilterType } from 'components/Grid/types';

import { DueDateRange } from '../components/DueDatePicker';
import { TypeFilterValue } from '../components/TypePicker';
import { AssignedToFilterValue } from '../components/AssignedToPicker';
import { MoreFiltersValue } from '../components/MoreFiltersPicker';

export const FILTER_FIELDS = {
  DUE_DATE: 'dueDate',
  TYPE: 'type',
  STATUS: 'status',
  ASSIGNED_TO: 'assignedTo',
  SITE: 'links_sites',
  ASSET: 'links_assets',
  CONTACT: 'links_contacts',
  START_DATE: 'startDate',
} as const;

export const FILTER_OPERATORS = {
  EQUALS: '=',
  IN: 'in',
  RANGE: '#>',
} as const;

export type FilterItem = { id: string; field: string; operator: string; value: unknown };

export const DEFAULT_STATUS_FILTER: (TaskStatus | ScheduleStatus)[] = [
  ScheduleStatus.Overdue,
  ScheduleStatus.Upcoming,
  TaskStatus.ToDo,
  TaskStatus.InProgress,
  TaskStatus.CantDo,
];

export const AUDITS_DEFAULT_STATUS_FILTER: (TaskStatus | ScheduleStatus)[] = [
  ScheduleStatus.Overdue,
  ScheduleStatus.Upcoming,
  TaskStatus.ToDo,
  TaskStatus.InProgress,
];

export const DEFAULT_FILTERS = {
  dueDate: { from: null, to: null } as DueDateRange,
  type: 'all' as TypeFilterValue,
  status: DEFAULT_STATUS_FILTER,
  assignedTo: [] as AssignedToFilterValue[],
  moreFilters: {
    site: null,
    asset: null,
    contact: null,
    startDate: { from: null, to: null },
  } as MoreFiltersValue,
};

export const MORE_FILTERS_FIELDS = [
  FILTER_FIELDS.SITE,
  FILTER_FIELDS.ASSET,
  FILTER_FIELDS.CONTACT,
  FILTER_FIELDS.START_DATE,
];

export interface SavedTaskFilters {
  dueDate: DueDateRange;
  type: TypeFilterValue;
  status: (TaskStatus | ScheduleStatus)[];
  assignedTo: AssignedToFilterValue[];
  moreFilters: MoreFiltersValue;
}

export interface UseTasksFiltersParams {
  gridApiRef: RefObject<GridApiPro>;
  refreshRef: MutableRefObject<Function | null>;
  user: { uuid: string; name: string } | null | undefined;
  defaultStatus?: (TaskStatus | ScheduleStatus)[];
  defaultDueDate?: DueDateRange;
  storageKey?: string;
}

export interface UseTasksFiltersReturn {
  initialFilters: FilterType[];
  dueDateFilter: DueDateRange;
  typeFilter: TypeFilterValue;
  statusFilter: (TaskStatus | ScheduleStatus)[];
  assignedToFilter: AssignedToFilterValue[];
  moreFilters: MoreFiltersValue;
  hasActiveFilters: boolean;
  handleDueDateChange: (value: DueDateRange) => void;
  handleTypeChange: (value: TypeFilterValue) => void;
  handleStatusChange: (value: (TaskStatus | ScheduleStatus)[]) => void;
  handleAssignedToChange: (value: AssignedToFilterValue[]) => void;
  handleMoreFiltersChange: (value: MoreFiltersValue) => void;
  handleResetFilters: () => void;
}

export const getDefaultAssignedTo = (
  user: { uuid: string; name: string } | null | undefined
): AssignedToFilterValue[] => (user ? [{ uuid: user.uuid, name: user.name }] : []);

export const buildFilterItems = {
  dueDate: (value: DueDateRange): FilterItem[] => {
    const hasFilter = value.from !== null || value.to !== null;
    return hasFilter
      ? [
          {
            id: FILTER_FIELDS.DUE_DATE,
            field: FILTER_FIELDS.DUE_DATE,
            operator: FILTER_OPERATORS.RANGE,
            value: [value.from ?? 0, value.to],
          },
        ]
      : [];
  },
  type: (value: TypeFilterValue): FilterItem[] =>
    value !== 'all'
      ? [{ id: FILTER_FIELDS.TYPE, field: FILTER_FIELDS.TYPE, operator: FILTER_OPERATORS.EQUALS, value }]
      : [],
  status: (values: (TaskStatus | ScheduleStatus)[]): FilterItem[] =>
    values.map((status) => ({
      id: `${FILTER_FIELDS.STATUS}_${status}`,
      field: FILTER_FIELDS.STATUS,
      operator: FILTER_OPERATORS.IN,
      value: status,
    })),
  assignedTo: (values: AssignedToFilterValue[]): FilterItem[] =>
    values.map((userItem) => ({
      id: `${FILTER_FIELDS.ASSIGNED_TO}_${userItem.uuid}`,
      field: FILTER_FIELDS.ASSIGNED_TO,
      operator: FILTER_OPERATORS.IN,
      value: userItem.uuid,
    })),
  moreFilters: (value: MoreFiltersValue): FilterItem[] => {
    const items: FilterItem[] = [];
    if (value.site) {
      items.push({
        id: FILTER_FIELDS.SITE,
        field: FILTER_FIELDS.SITE,
        operator: FILTER_OPERATORS.EQUALS,
        value: value.site.uuid,
      });
    }
    if (value.asset) {
      items.push({
        id: FILTER_FIELDS.ASSET,
        field: FILTER_FIELDS.ASSET,
        operator: FILTER_OPERATORS.EQUALS,
        value: value.asset.uuid,
      });
    }
    if (value.contact) {
      items.push({
        id: FILTER_FIELDS.CONTACT,
        field: FILTER_FIELDS.CONTACT,
        operator: FILTER_OPERATORS.EQUALS,
        value: value.contact.uuid,
      });
    }
    if (value.startDate.from !== null || value.startDate.to !== null) {
      items.push({
        id: FILTER_FIELDS.START_DATE,
        field: FILTER_FIELDS.START_DATE,
        operator: FILTER_OPERATORS.RANGE,
        value: [value.startDate.from, value.startDate.to],
      });
    }
    return items;
  },
};

export const arraysEqual = <T>(a: T[], b: T[]): boolean => a.length === b.length && a.every((item) => b.includes(item));
